(() => {
  const selectors = {
    builder: '[data-ray-mm-builder]',
    card: '[data-ray-mm-card]',
    openProduct: '[data-ray-mm-open-product]',
    cardSelectedCount: '[data-ray-mm-card-selected-count]',
    selectedItems: '[data-ray-mm-selected-items]',
    slots: '[data-ray-mm-slots]',
    slotsToggle: '[data-ray-mm-slots-toggle]',
    groupNavLink: '[data-ray-mm-group-nav-link]',
    group: '[data-ray-mm-group]',
    counter: '[data-ray-mm-counter]',
    mobileCounter: '[data-ray-mm-mobile-counter]',
    progressWrap: '[data-ray-mm-progress-wrap]',
    progress: '[data-ray-mm-progress]',
    progressBadge: '[data-ray-mm-progress-badge]',
    subtotal: '[data-ray-mm-subtotal]',
    discountPreview: '[data-ray-mm-discount-preview]',
    discountLabel: '[data-ray-mm-discount-label]',
    discountAmount: '[data-ray-mm-discount-amount]',
    discountTotal: '[data-ray-mm-discount-total]',
    message: '[data-ray-mm-message]',
    addBundle: '[data-ray-mm-add-bundle]',
    mobileSubmit: '[data-ray-mm-mobile-submit]',
    modal: '[data-ray-mm-modal]',
    modalClose: '[data-ray-mm-modal-close]',
    modalImage: '[data-ray-mm-modal-image]',
    modalTitle: '[data-ray-mm-modal-title]',
    modalPrice: '[data-ray-mm-modal-price]',
    modalPicker: '[data-ray-mm-modal-picker]',
    modalStatus: '[data-ray-mm-modal-status]',
    modalAdd: '[data-ray-mm-modal-add]',
    modalLink: '[data-ray-mm-modal-link]',
    sizeGuide: '[data-ray-mm-size-guide]',
    sizeGuideClose: '[data-ray-mm-size-guide-close]',
    sizeGuideImages: '[data-ray-mm-size-guide-images]'
  };

  const classes = {
    error: 'is-error',
    success: 'is-success'
  };

  class RayMixMatchBuilder {
    constructor(root) {
      this.root = root;
      this.requiredCount = Number(root.dataset.requiredCount || 6);
      this.bundleTitle = root.dataset.bundleTitle || `Mix & Match — ${this.requiredCount} речей`;
      this.rootUrl = root.dataset.rootUrl || '/';
      this.currency = root.dataset.currency || 'UAH';
      this.afterAdd = root.dataset.afterAdd || 'cart';
      this.addBundleText = root.dataset.addBundleText || 'Додати набір у кошик';
      this.addMoreText = root.dataset.addMoreText || 'Додайте ще';

      this.cards = [];
      this.selectedItems = new Map();

      this.activeCard = null;
      this.activeProduct = null;
      this.activeVariant = null;
      this.pickerObserver = null;
      this.groupObserver = null;
      this.syncTimeouts = [];
      this.areSlotsExpanded = false;

      this.selectedItemsEl = root.querySelector(selectors.selectedItems);
      this.slotsEl = root.querySelector(selectors.slots);
      this.slotsToggleButton = root.querySelector(selectors.slotsToggle);
      this.groupNavLinks = Array.from(root.querySelectorAll(selectors.groupNavLink));
      this.groups = Array.from(root.querySelectorAll(selectors.group));
      this.counterEl = root.querySelector(selectors.counter);
      this.mobileCounterEl = root.querySelector(selectors.mobileCounter);
      this.progressWrapEl = root.querySelector(selectors.progressWrap);
      this.progressEl = root.querySelector(selectors.progress);
      this.progressBadgeEl = root.querySelector(selectors.progressBadge);
      this.subtotalEl = root.querySelector(selectors.subtotal);
      this.discountPreviewEl = root.querySelector(selectors.discountPreview);
      this.discountLabelEl = root.querySelector(selectors.discountLabel);
      this.discountAmountEl = root.querySelector(selectors.discountAmount);
      this.discountTotalEl = root.querySelector(selectors.discountTotal);
      this.messageEl = root.querySelector(selectors.message);
      this.addBundleButton = root.querySelector(selectors.addBundle);
      this.mobileSubmitButton = root.querySelector(selectors.mobileSubmit);

      this.modal = root.querySelector(selectors.modal);
      this.modalImage = root.querySelector(selectors.modalImage);
      this.modalTitle = root.querySelector(selectors.modalTitle);
      this.modalPrice = root.querySelector(selectors.modalPrice);
      this.modalPicker = root.querySelector(selectors.modalPicker);
      this.modalStatus = root.querySelector(selectors.modalStatus);
      this.modalAdd = root.querySelector(selectors.modalAdd);
      this.modalLink = root.querySelector(selectors.modalLink);

      this.sizeGuide = root.querySelector(selectors.sizeGuide);
      this.sizeGuideImages = root.querySelector(selectors.sizeGuideImages);

      this.completeText = 'Набір готовий. Можна додавати в кошик.';
      this.chooseVariantText = this.modalAdd ? this.modalAdd.textContent.trim() : 'Оберіть розмір/колір';
      this.addItemText = 'Додати в набір';

      this.init();
    }

    init() {
      this.initCards();
      this.initModal();
      this.initSizeGuide();
      this.initSlotsToggle();
      this.initGroupNavigation();
      this.initMobileSummaryJump();
      this.initBundleButtons();
      this.updateSummary();
    }

    initGroupNavigation() {
      if (!this.groupNavLinks.length || !this.groups.length) return;

      this.groupNavLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          const targetId = link.getAttribute('href');

          if (!targetId || !targetId.startsWith('#')) return;

          const target = this.root.querySelector(targetId);

          if (!target) return;

          event.preventDefault();

          this.setActiveGroupNavLink(targetId, true);

          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        });
      });

      if (!('IntersectionObserver' in window)) return;

      this.groupObserver = new IntersectionObserver(
        (entries) => {
          const visibleEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (!visibleEntry || !visibleEntry.target.id) return;

          this.setActiveGroupNavLink(`#${visibleEntry.target.id}`);
        },
        {
          root: null,
          rootMargin: '-35% 0px -55% 0px',
          threshold: [0.15, 0.3, 0.5, 0.75]
        }
      );

      this.groups.forEach((group) => {
        this.groupObserver.observe(group);
      });
    }

    setActiveGroupNavLink(targetId, shouldScrollNav = false) {
      this.groupNavLinks.forEach((link) => {
        const isActive = link.getAttribute('href') === targetId;
        link.classList.toggle('is-active', isActive);

        if (!isActive || !shouldScrollNav) return;

        const nav = link.closest('[data-ray-mm-group-nav]');

        if (!nav) return;

        const linkLeft = link.offsetLeft;
        const linkWidth = link.offsetWidth;
        const navWidth = nav.clientWidth;

        nav.scrollTo({
          left: linkLeft - navWidth / 2 + linkWidth / 2,
          behavior: 'smooth'
        });
      });
    }

    initCards() {
      const cardElements = Array.from(this.root.querySelectorAll(selectors.card));

      cardElements.forEach((card) => {
        const openButton = card.querySelector(selectors.openProduct);

        if (!openButton) return;

        const productData = this.readProductData(openButton);

        if (!productData) return;

        const state = {
          el: card,
          product: productData,
          openButton,
          wasInitiallyDisabled: openButton.disabled,
          selectedCountEl: card.querySelector(selectors.cardSelectedCount)
        };

        this.cards.push(state);

        openButton.addEventListener('click', () => {
          this.openProductModal(state);
        });
      });
    }

    initModal() {
      if (!this.modal) return;

      this.root.querySelectorAll(selectors.modalClose).forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      if (this.modalAdd) {
        this.modalAdd.addEventListener('click', () => {
          this.addActiveVariantToBundle();
        });
      }

      if (this.modalPicker) {
        this.modalPicker.addEventListener('change', () => {
          this.sanitizePicker();
          this.schedulePickerSync();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (this.sizeGuide && !this.sizeGuide.hasAttribute('hidden')) {
            this.closeSizeGuide();
            return;
          }

          if (this.modal && !this.modal.hasAttribute('hidden')) {
            this.closeModal();
          }
        }
      });
    }

    initSizeGuide() {
      if (!this.sizeGuide) return;

      this.root.querySelectorAll(selectors.sizeGuideClose).forEach((button) => {
        button.addEventListener('click', () => this.closeSizeGuide());
      });

      this.root.addEventListener(
        'click',
        (event) => {
          const target = event.target;

          const trigger = target.closest(
            'a, button, [role="button"], modal-opener, .size-chart, .size-guide, [data-open-size-chart], [data-size-chart], [data-popup]'
          );

          if (!trigger) return;

          const triggerText = trigger.textContent.trim().toLowerCase();

          const isSizeGuideTrigger =
            triggerText.includes('таблиця розмірів') ||
            triggerText.includes('таблица размеров') ||
            triggerText.includes('size chart') ||
            triggerText.includes('size guide');

          if (!isSizeGuideTrigger) return;

          const isInsideMixMatchPicker = trigger.closest('.ray-mm-modal__picker');

          if (!isInsideMixMatchPicker) return;

          const currentOption = trigger.closest('.variant-option');
          const picker = trigger.closest('variant-picker');

          let sizeGuideIndex = 0;

          if (currentOption && picker) {
            const sizeOptions = Array.from(picker.querySelectorAll('.variant-option')).filter((option) => {
              return !option.classList.contains('variant-option--swatches');
            });

            const foundIndex = sizeOptions.indexOf(currentOption);

            if (foundIndex >= 0) {
              sizeGuideIndex = foundIndex;
            }
          }

          event.preventDefault();
          event.stopPropagation();

          this.openSizeGuide(sizeGuideIndex);
        },
        true
      );
    }

    initSlotsToggle() {
      if (!this.slotsToggleButton) return;

      this.slotsToggleButton.addEventListener('click', () => {
        this.areSlotsExpanded = !this.areSlotsExpanded;
        this.renderSlots();
      });
    }

    initMobileSummaryJump() {
      if (!this.mobileCounterEl) return;

      this.mobileCounterEl.addEventListener('click', () => {
        const summary = this.root.querySelector('[data-ray-mm-summary]');

        if (!summary) return;

        summary.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    }

    readProductData(button) {
      const jsonId = button.dataset.productJsonId;
      const script = jsonId ? document.getElementById(jsonId) : null;

      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Ray Mix & Match: invalid product JSON', error);
        return null;
      }
    }

    openProductModal(cardState) {
      if (!this.modal) return;

      this.activeCard = cardState;
      this.activeProduct = cardState.product;
      this.activeVariant = this.getInitialVariant(this.activeProduct);

      if (this.modalTitle) {
        this.modalTitle.textContent = this.activeProduct.title || '';
      }

      if (this.modalStatus) {
        this.modalStatus.hidden = true;
        this.modalStatus.textContent = '';
        this.modalStatus.classList.remove(classes.error, classes.success);
      }

      this.mountPickerTemplate(cardState.openButton.dataset.pickerTemplateId);
      this.updateModalByVariant(this.activeVariant);
      this.schedulePickerSync();
      this.updateModalAddButton();

      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ray-mm-modal-open');
    }

    closeModal() {
      if (!this.modal) return;

      this.closeSizeGuide();

      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ray-mm-modal-open');

      this.clearSyncTimeouts();
      this.pickerObserver?.disconnect();
      this.pickerObserver = null;

      if (this.modalPicker) {
        this.modalPicker.innerHTML = '';
      }

      this.activeCard = null;
      this.activeProduct = null;
      this.activeVariant = null;
    }

    openSizeGuide(sizeGuideIndex = 0) {
      if (!this.sizeGuide || !this.sizeGuideImages) return;

      const images = Array.isArray(this.activeProduct?.sizeGuideImages)
        ? this.activeProduct.sizeGuideImages.filter(Boolean)
        : [];

      const selectedImage = images[sizeGuideIndex] || images[0];

      if (!selectedImage) {
        this.sizeGuideImages.innerHTML = `
          <p class="ray-mm-size-guide__empty">
            Для цього товару таблиця розмірів ще не додана.
          </p>
        `;
      } else {
        this.sizeGuideImages.innerHTML = `
          <img
            class="ray-mm-size-guide__image"
            src="${this.escapeHtml(selectedImage)}"
            alt="Таблиця розмірів"
            loading="lazy"
          >
        `;
      }

      this.sizeGuide.removeAttribute('hidden');
      this.sizeGuide.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ray-mm-modal-open');
    }

    closeSizeGuide() {
      if (!this.sizeGuide) return;

      this.sizeGuide.setAttribute('hidden', '');
      this.sizeGuide.setAttribute('aria-hidden', 'true');

      if (this.modal && !this.modal.hasAttribute('hidden')) {
        document.body.classList.add('ray-mm-modal-open');
      } else {
        document.body.classList.remove('ray-mm-modal-open');
      }
    }

    mountPickerTemplate(templateId) {
      if (!this.modalPicker) return;

      this.pickerObserver?.disconnect();
      this.modalPicker.innerHTML = '';

      const template = templateId ? document.getElementById(templateId) : null;

      if (!template) return;

      this.modalPicker.appendChild(template.content.cloneNode(true));
      this.sanitizePicker();

      this.pickerObserver = new MutationObserver(() => {
        this.sanitizePicker();
      });

      this.pickerObserver.observe(this.modalPicker, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-template-product-match']
      });
    }

    sanitizePicker() {
      if (!this.modalPicker) return;

      const picker = this.modalPicker.querySelector('variant-picker');

      if (!picker) return;

      picker.removeAttribute('data-template-product-match');
    }

    schedulePickerSync() {
      this.clearSyncTimeouts();

      [40, 180, 450, 900].forEach((delay) => {
        const timeoutId = setTimeout(() => {
          this.syncModalFromPicker();
        }, delay);

        this.syncTimeouts.push(timeoutId);
      });
    }

    clearSyncTimeouts() {
      this.syncTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      this.syncTimeouts = [];
    }

    syncModalFromPicker() {
      this.sanitizePicker();

      const pickerVariant = this.readVariantFromPicker();

      if (pickerVariant && pickerVariant.id) {
        this.activeVariant = this.mergeVariantData(pickerVariant);
        this.updateModalByVariant(this.activeVariant);
        this.updateModalAddButton();
      }
    }

    readVariantFromPicker() {
      if (!this.modalPicker) return null;

      const picker = this.modalPicker.querySelector('variant-picker');
      const jsonScript = picker ? picker.querySelector('script[type="application/json"]') : null;

      if (!jsonScript || !jsonScript.textContent.trim()) return null;

      try {
        return this.normalizeVariantFromPicker(JSON.parse(jsonScript.textContent));
      } catch (error) {
        console.warn('Ray Mix & Match: picker JSON not ready', error);
        return null;
      }
    }

    normalizeVariantFromPicker(variant) {
      if (!variant) return null;

      const featuredImage =
        variant.featured_image?.src ||
        variant.featured_image ||
        variant.featured_media?.preview_image?.src ||
        variant.featured_media?.preview_image ||
        null;

      return {
        id: variant.id,
        title: variant.title,
        available: variant.available,
        price: variant.price,
        compareAtPrice: variant.compare_at_price || variant.compareAtPrice || 0,
        image: featuredImage,
        url: variant.url
      };
    }

    mergeVariantData(pickerVariant) {
      if (!pickerVariant || !this.activeProduct) return pickerVariant;

      const matchingVariant = this.activeProduct.variants.find((variant) => {
        return String(variant.id) === String(pickerVariant.id);
      });

      if (!matchingVariant) {
        return {
          ...pickerVariant,
          priceFormatted: this.formatMoney(pickerVariant.price),
          title: pickerVariant.title || ''
        };
      }

      return {
        ...matchingVariant,
        ...pickerVariant,
        priceFormatted: matchingVariant.priceFormatted || this.formatMoney(pickerVariant.price),
        image: pickerVariant.image || matchingVariant.image || this.activeProduct.featuredImage
      };
    }

    updateModalByVariant(variant) {
      if (!this.activeProduct) return;

      const image = variant && variant.image ? variant.image : this.activeProduct.featuredImage;

      if (this.modalImage) {
        this.modalImage.src = image || '';
        this.modalImage.alt = (variant && variant.imageAlt) || this.activeProduct.featuredImageAlt || this.activeProduct.title || '';
      }

      if (this.modalPrice) {
        if (variant && variant.priceFormatted) {
          this.modalPrice.textContent = variant.priceFormatted;
        } else if (variant && typeof variant.price !== 'undefined') {
          this.modalPrice.textContent = this.formatMoney(variant.price);
        } else {
          this.modalPrice.textContent = this.activeProduct.priceFormatted || '';
        }
      }

      if (this.modalLink) {
        const productUrl = this.getPickerProductUrl() || this.activeProduct.url || '#';

        if (variant && variant.id && productUrl !== '#') {
          this.modalLink.href = `${productUrl}${productUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
        } else {
          this.modalLink.href = productUrl;
        }
      }
    }

    getPickerProductUrl() {
      if (!this.modalPicker) return null;

      const picker = this.modalPicker.querySelector('variant-picker');

      return picker?.dataset.productUrl || null;
    }

    updateModalAddButton() {
      if (!this.modalAdd) return;

      const total = this.getSelectedTotal();
      const isBundleFull = total >= this.requiredCount;
      const hasVariant = this.activeVariant && this.activeVariant.id;
      const isAvailable = hasVariant && this.activeVariant.available !== false;

      this.modalAdd.disabled = isBundleFull || !hasVariant || !isAvailable;

      if (isBundleFull) {
        this.modalAdd.textContent = 'Набір уже заповнений';
        return;
      }

      if (!hasVariant || !isAvailable) {
        this.modalAdd.textContent = this.chooseVariantText;
        return;
      }

      this.modalAdd.textContent = this.addItemText;
    }

    addActiveVariantToBundle() {
      if (!this.activeProduct || !this.activeVariant || !this.activeVariant.id) {
        this.setModalStatus('Оберіть доступний варіант товару.', 'error');
        return;
      }

      if (this.activeVariant.available === false) {
        this.setModalStatus('Цей варіант недоступний.', 'error');
        return;
      }

      if (this.getSelectedTotal() >= this.requiredCount) {
        this.setModalStatus('Набір уже заповнений.', 'error');
        return;
      }

      this.addSelectedVariant(this.activeProduct, this.activeVariant);
      this.closeModal();
    }

    addSelectedVariant(product, variant) {
      const key = String(variant.id);
      const existing = this.selectedItems.get(key);
      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';

      if (existing) {
        existing.quantity += 1;
      } else {
        this.selectedItems.set(key, {
          productId: product.id,
          variantId: variant.id,
          productTitle: product.title,
          variantTitle,
          image: variant.image || product.featuredImage,
          price: Number(variant.price || 0),
          priceFormatted: variant.priceFormatted || this.formatMoney(variant.price),
          quantity: 1
        });
      }

      this.updateSummary();
      this.updateCardSelectedCounts();
    }

    incrementSelectedItem(variantId) {
      if (this.getSelectedTotal() >= this.requiredCount) return;

      const existing = this.selectedItems.get(String(variantId));

      if (!existing) return;

      existing.quantity += 1;

      this.updateSummary();
      this.updateCardSelectedCounts();
    }

    removeOneSelectedItem(variantId) {
      const key = String(variantId);
      const existing = this.selectedItems.get(key);

      if (!existing) return;

      if (existing.quantity > 1) {
        existing.quantity -= 1;
      } else {
        this.selectedItems.delete(key);
      }

      this.updateSummary();
      this.updateCardSelectedCounts();
    }

    updateSummary() {
      const total = this.getSelectedTotal();
      const subtotal = this.getSubtotal();
      const progress = Math.min((total / this.requiredCount) * 100, 100);
      const remaining = Math.max(this.requiredCount - total, 0);
      const isComplete = total === this.requiredCount;

      if (this.counterEl) {
        this.counterEl.textContent = `${total} / ${this.requiredCount}`;
      }

      if (this.mobileCounterEl) {
        this.mobileCounterEl.textContent = `${total} / ${this.requiredCount}`;
      }

      if (this.progressEl) {
        this.progressEl.style.width = `${progress}%`;
      }

      if (this.progressWrapEl) {
        this.progressWrapEl.style.setProperty('--ray-mm-progress-position', `${progress}%`);
      }

      if (this.progressBadgeEl) {
        this.progressBadgeEl.textContent = remaining;
      }

      if (this.subtotalEl) {
        this.subtotalEl.textContent = total > 0 ? this.formatMoney(subtotal) : '—';
      }

      this.updateDiscountPreview({
        subtotal,
        isComplete
      });

      if (this.addBundleButton) {
        this.addBundleButton.disabled = !isComplete;
        this.addBundleButton.textContent = isComplete
          ? this.addBundleText
          : `${this.addMoreText} ${remaining} ${this.pluralizeThing(remaining)}`;
      }

      if (this.mobileSubmitButton) {
        this.mobileSubmitButton.disabled = !isComplete;
        this.mobileSubmitButton.textContent = isComplete
          ? this.addBundleText
          : `${this.addMoreText} ${remaining} ${this.pluralizeThing(remaining)}`;
      }

      if (this.messageEl) {
        this.messageEl.classList.remove(classes.error, classes.success);

        if (isComplete) {
          this.messageEl.classList.add(classes.success);
          this.messageEl.textContent = this.completeText;
        } else {
          this.messageEl.textContent = `Оберіть ${this.requiredCount} речей, щоб додати набір у кошик.`;
        }
      }

      this.renderSlots();
      this.renderSelectedItems();
      this.updateCardSelectedCounts();
    }

    getBundleDiscountPercent() {
      if (this.requiredCount === 4) return 3;
      if (this.requiredCount === 6) return 5;
      if (this.requiredCount === 8) return 7;

      return 0;
    }

    updateDiscountPreview({ subtotal, isComplete }) {
      if (!this.discountPreviewEl) return;

      const discountPercent = this.getBundleDiscountPercent();
      const shouldShow = isComplete && discountPercent > 0 && subtotal > 0;

      this.discountPreviewEl.hidden = !shouldShow;

      if (!shouldShow) {
        if (this.discountLabelEl) this.discountLabelEl.textContent = 'Знижка набору';
        if (this.discountAmountEl) this.discountAmountEl.textContent = '—';
        if (this.discountTotalEl) this.discountTotalEl.textContent = '—';
        return;
      }

      const discountAmount = Math.round((subtotal * discountPercent) / 100);
      const discountedTotal = Math.max(subtotal - discountAmount, 0);

      if (this.discountLabelEl) {
        this.discountLabelEl.textContent = `Знижка набору ${discountPercent}%`;
      }

      if (this.discountAmountEl) {
        this.discountAmountEl.textContent = `−${this.formatMoney(discountAmount)}`;
      }

      if (this.discountTotalEl) {
        this.discountTotalEl.textContent = this.formatMoney(discountedTotal);
      }
    }

    renderSlots() {
      if (!this.slotsEl) return;

      const expandedItems = this.getExpandedSelectedItems();
      const shouldCollapse = this.requiredCount > 4;
      const visibleCount = shouldCollapse && !this.areSlotsExpanded ? 4 : this.requiredCount;
      const slots = [];

      this.slotsEl.classList.toggle('is-expanded', this.areSlotsExpanded);

      for (let index = 0; index < visibleCount; index += 1) {
        const item = expandedItems[index];

        if (item) {
          slots.push(`
            <div class="ray-mm-summary-slot is-filled">
              ${
                item.image
                  ? `<img class="ray-mm-summary-slot__image" src="${item.image}" alt="${this.escapeHtml(item.productTitle)}" loading="lazy">`
                  : ''
              }
              <span class="ray-mm-summary-slot__number">${index + 1}</span>
              <button
                type="button"
                class="ray-mm-summary-slot__remove"
                data-ray-mm-slot-remove
                data-variant-id="${item.variantId}"
                aria-label="Видалити товар із набору"
              >
                ×
              </button>
            </div>
          `);
        } else {
          slots.push(`
            <div class="ray-mm-summary-slot">
              <span class="ray-mm-summary-slot__plus">+</span>
            </div>
          `);
        }
      }

      this.slotsEl.innerHTML = slots.join('');

      this.slotsEl.querySelectorAll('[data-ray-mm-slot-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          this.removeOneSelectedItem(button.dataset.variantId);
        });
      });

      if (this.slotsToggleButton) {
        if (shouldCollapse) {
          this.slotsToggleButton.hidden = false;
          this.slotsToggleButton.textContent = this.areSlotsExpanded ? 'Згорнути' : `Показати всі ${this.requiredCount}`;
        } else {
          this.slotsToggleButton.hidden = true;
        }
      }
    }

    renderSelectedItems() {
      if (!this.selectedItemsEl) return;

      this.selectedItemsEl.innerHTML = '';
    }

    updateCardSelectedCounts() {
      const isBundleFull = this.getSelectedTotal() >= this.requiredCount;

      this.cards.forEach((card) => {
        const quantity = Array.from(this.selectedItems.values()).reduce((total, item) => {
          if (Number(item.productId) === Number(card.product.id)) {
            return total + item.quantity;
          }

          return total;
        }, 0);

        card.el.classList.toggle('is-selected', quantity > 0);

        if (card.selectedCountEl) {
          card.selectedCountEl.hidden = true;
          card.selectedCountEl.textContent = '';
        }

        if (card.openButton) {
          const shouldDisable = card.wasInitiallyDisabled || isBundleFull;

          card.openButton.disabled = shouldDisable;
          card.openButton.classList.toggle('is-bundle-full-disabled', isBundleFull && !card.wasInitiallyDisabled);
        }
      });
    }

    initBundleButtons() {
      if (this.addBundleButton) {
        this.addBundleButton.addEventListener('click', () => this.addBundleToCart());
      }

      if (this.mobileSubmitButton) {
        this.mobileSubmitButton.addEventListener('click', () => this.addBundleToCart());
      }
    }

    async addBundleToCart() {
      const total = this.getSelectedTotal();

      if (total !== this.requiredCount) {
        this.setGlobalMessage(`Потрібно обрати рівно ${this.requiredCount} речей.`, 'error');
        return;
      }

      this.setLoading(true);

      try {
        const currentCart = await this.getCart();
        const hasExistingMixMatchBundle = this.cartHasMixMatchBundle(currentCart);

        if (hasExistingMixMatchBundle) {
          this.setGlobalMessage('У кошику вже є Mix & Match набір. Видаліть його, щоб зібрати інший.', 'error');
        } else {
          const bundleId = this.createBundleId();

          const items = Array.from(this.selectedItems.values()).map((item, index) => {
            return {
              id: item.variantId,
              quantity: item.quantity,
              properties: {
                _ray_bundle_id: bundleId,
                _ray_bundle_type: 'mix_match',
                _ray_bundle_size: String(this.requiredCount),
                _ray_bundle_title: this.bundleTitle,
                _ray_bundle_index: String(index + 1)
              }
            };
          });

          const addResponse = await fetch(this.getCartAddUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ items })
          });

          const addedData = await addResponse.json();

          if (!addResponse.ok) {
            const errorMessage =
              addedData && addedData.description ? addedData.description : 'Не вдалося додати набір у кошик.';

            throw new Error(errorMessage);
          }

          const cart = await this.getCart();

          this.dispatchHorizonCartUpdate(cart);
          await this.waitForAnimationFrames(3);

          this.dispatchHorizonCartAdd(cart, addedData);
          await this.waitForAnimationFrames(2);

          this.openDrawerHardFallback();

          this.resetBuilderAfterSuccessfulAdd();
          this.setGlobalMessage('Набір додано в кошик. Можеш зібрати наступний після видалення поточного набору.', 'success');
        }
      } catch (error) {
        console.error('Ray Mix & Match: add to cart error', error);
        this.setGlobalMessage(error.message || 'Не вдалося додати набір у кошик.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    resetBuilderAfterSuccessfulAdd() {
      this.selectedItems.clear();
      this.areSlotsExpanded = false;

      this.updateSummary();
      this.updateCardSelectedCounts();

      if (this.modal && !this.modal.hasAttribute('hidden')) {
        this.closeModal();
      }
    }

    cartHasMixMatchBundle(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;

      return cart.items.some((item) => {
        const properties = item.properties || {};

        if (properties._ray_bundle_id || properties._ray_bundle_type === 'mix_match') {
          return true;
        }

        if (Array.isArray(properties)) {
          return properties.some((property) => {
            return (
              property &&
              (property.name === '_ray_bundle_id' ||
                property.name === '_ray_bundle_type' ||
                property[0] === '_ray_bundle_id' ||
                property[0] === '_ray_bundle_type')
            );
          });
        }

        return false;
      });
    }

    async getCart() {
      const response = await fetch(this.getCartJsonUrl(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Не вдалося отримати кошик. Перевір інтернет і спробуй ще раз.');
      }

      return response.json();
    }

    dispatchHorizonCartUpdate(cart) {
      const detail = {
        data: {
          itemCount: cart.item_count,
          source: 'product-form-component',
          cart
        },
        cart
      };

      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));

      window.dispatchEvent(new CustomEvent('cart:update', { detail }));
      window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    }

    dispatchHorizonCartAdd(cart, addedData) {
      const addedItem = Array.isArray(addedData?.items) ? addedData.items[0] : addedData;

      const detail = {
        data: {
          itemCount: cart.item_count,
          source: 'product-form-component',
          cart,
          item: addedItem
        },
        cart,
        addedData,
        addedItem
      };

      document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true, detail }));

      window.dispatchEvent(new CustomEvent('cart:add', { detail }));
      window.dispatchEvent(new CustomEvent('cart:added', { detail }));
    }

    openDrawerHardFallback() {
      const cartDrawerComponent = document.querySelector('cart-drawer-component');

      if (cartDrawerComponent && typeof cartDrawerComponent.open === 'function') {
        cartDrawerComponent.open();
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }

      if (cartDrawer) {
        cartDrawer.setAttribute('open', '');
        cartDrawer.classList.add('active', 'is-open', 'is-visible');
        document.body.classList.add('overflow-hidden', 'cart-drawer-open');
        return;
      }

      const drawer =
        document.querySelector('#CartDrawer') ||
        document.querySelector('#cart-drawer') ||
        document.querySelector('[data-cart-drawer]') ||
        document.querySelector('[data-cart-drawer-component]');

      if (drawer) {
        if (typeof drawer.showModal === 'function') {
          drawer.showModal();
          return;
        }

        drawer.removeAttribute('hidden');
        drawer.setAttribute('open', '');
        drawer.classList.add('active', 'is-open', 'is-visible');
        document.body.classList.add('overflow-hidden', 'cart-drawer-open');
        return;
      }

      const trigger =
        document.querySelector('[aria-controls="CartDrawer"]') ||
        document.querySelector('[aria-controls="cart-drawer"]') ||
        document.querySelector('[data-cart-drawer-trigger]') ||
        document.querySelector('[data-cart-trigger]') ||
        document.querySelector('cart-drawer-opener button') ||
        document.querySelector('button[name="cart"]');

      if (trigger) {
        trigger.click();
      }
    }

    waitForAnimationFrames(count = 1) {
      return new Promise((resolve) => {
        const next = (remaining) => {
          if (remaining <= 0) {
            resolve();
            return;
          }

          requestAnimationFrame(() => next(remaining - 1));
        };

        next(count);
      });
    }

    getInitialVariant(product) {
      if (!product || !Array.isArray(product.variants)) return null;

      return (
        product.variants.find((variant) => String(variant.id) === String(product.selectedVariantId)) ||
        product.variants.find((variant) => variant.available) ||
        product.variants[0] ||
        null
      );
    }

    getExpandedSelectedItems() {
      const expanded = [];

      Array.from(this.selectedItems.values()).forEach((item) => {
        for (let index = 0; index < item.quantity; index += 1) {
          expanded.push(item);
        }
      });

      return expanded;
    }

    getSelectedTotal() {
      return Array.from(this.selectedItems.values()).reduce((total, item) => {
        return total + item.quantity;
      }, 0);
    }

    getSubtotal() {
      return Array.from(this.selectedItems.values()).reduce((total, item) => {
        return total + item.price * item.quantity;
      }, 0);
    }

    getCartAddUrl() {
      const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
      return `${root}cart/add.js`;
    }

    getCartJsonUrl() {
      const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
      return `${root}cart.js`;
    }

    getCartUrl() {
      const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
      return `${root}cart`;
    }

    createBundleId() {
      return `ray-mm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    setLoading(isLoading) {
      const isComplete = this.getSelectedTotal() === this.requiredCount;

      [this.addBundleButton, this.mobileSubmitButton].filter(Boolean).forEach((button) => {
        button.disabled = isLoading || !isComplete;
        button.classList.toggle('is-loading', isLoading);
      });
    }

    setModalStatus(text, type) {
      if (!this.modalStatus) return;

      this.modalStatus.hidden = false;
      this.modalStatus.classList.remove(classes.error, classes.success);

      if (type === 'error') {
        this.modalStatus.classList.add(classes.error);
      }

      if (type === 'success') {
        this.modalStatus.classList.add(classes.success);
      }

      this.modalStatus.textContent = text;
    }

    setGlobalMessage(text, type) {
      if (!this.messageEl) return;

      this.messageEl.classList.remove(classes.error, classes.success);

      if (type === 'error') {
        this.messageEl.classList.add(classes.error);
      }

      if (type === 'success') {
        this.messageEl.classList.add(classes.success);
      }

      this.messageEl.textContent = text;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'uk-UA', {
          style: 'currency',
          currency: this.currency
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${this.currency}`;
      }
    }

    pluralizeThing(number) {
      const normalized = Math.abs(Number(number || 0));
      const lastDigit = normalized % 10;
      const lastTwoDigits = normalized % 100;

      if (lastDigit === 1 && lastTwoDigits !== 11) return 'річ';

      if ([2, 3, 4].includes(lastDigit) && ![12, 13, 14].includes(lastTwoDigits)) {
        return 'речі';
      }

      return 'речей';
    }

    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  const initRayMixMatchBuilders = () => {
    document.querySelectorAll(selectors.builder).forEach((root) => {
      if (root.dataset.rayMmInitialized === 'true') return;

      root.dataset.rayMmInitialized = 'true';
      new RayMixMatchBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRayMixMatchBuilders);
  } else {
    initRayMixMatchBuilders();
  }

  document.addEventListener('shopify:section:load', initRayMixMatchBuilders);
})();